import { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react'

export type SortKey = 'start_date' | 'price'

export interface Filters {
  date: string[]
  departure: string[]
  ship: string[]
  guidance: string[]
  availability: string[]
}

export interface FilterConfiguration {
  filters: Filters
  sort: SortKey
}

const makeDefaultFilters = (): FilterConfiguration => {
  return {
    filters: {
      date: [],
      departure: [],
      ship: [],
      guidance: [],
      availability: []
    },
    sort: 'start_date'
  }
}

type SetFilter = <T extends keyof FilterConfiguration['filters'], >(filter: T, value: FilterConfiguration['filters'][T]) => void
type SetSort = (sort: SortKey) => void
type ClearFilters = () => void

type FilterContextType = FilterConfiguration & {
  setFilter: SetFilter
  setSort: SetSort
  clearFilters: ClearFilters
}

export const FilterContext = createContext<FilterContextType>({
  ...makeDefaultFilters(),
  setFilter: () => {
  },
  setSort: () => {
  },
  clearFilters: () => {
  }
})

export const useFilters = (): FilterContextType => useContext(FilterContext)

export function FilterProvider ({ children }: PropsWithChildren<object>): JSX.Element {
  const [{ filters, sort }, setFilters] = useState(makeDefaultFilters())
  const setFilter = useCallback(<T extends keyof FilterConfiguration['filters'], >(filter: T, value: FilterConfiguration['filters'][T]) => {
    setFilters(({ filters, sort }) => {
      return {
        filters: {
          ...filters,
          [filter]: value
        },
        sort
      }
    })
  }, [])
  const setSort = useCallback((sort: SortKey) => {
    setFilters(({ filters }) => ({
      filters, sort
    }))
  }, [])
  const clearFilters = useCallback(() => {
    setFilters(({ sort }): FilterConfiguration => ({
      filters: makeDefaultFilters().filters,
      sort
    }))
  }, [])
  const value = useMemo(() => {
    return {
      filters,
      sort,
      setFilter,
      setSort,
      clearFilters
    }
  }, [filters, setFilter, sort, setSort, clearFilters])
  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
}
